'use client'

import { useEffect, useState } from 'react'

import { writeDrawn } from '@/features/landing/stroke/stroke-clock'
import { StrokeDraw } from '@/features/landing/stroke/stroke-draw'
import { cn } from '@/lib/utils'

function reducedMotion(): boolean {
    return (
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
    )
}

// Spec §3.2 "Written": the open door's glyph is inked; a gated door's glyph
// is a guideline that writes itself in true stroke order when reached —
// one stroke at a time, the next waiting for the last — and returns to
// guideline when left. Drawn from KanjiVG strokes, never the mincho face.
export function WrittenGlyph({
    strokePaths,
    viewBox,
    open,
    reached,
    className,
}: {
    strokePaths: string[]
    viewBox: string
    open: boolean
    reached: boolean
    className?: string
}) {
    const n = strokePaths.length
    const full = strokePaths.map(() => 1)
    const none = strokePaths.map(() => 0)
    const [written, setWritten] = useState<number[]>(none)
    const [fading, setFading] = useState(false)
    // Open doors and reduced-motion reached doors are stills: no effect, no writing.
    const still = open || (reached && reducedMotion())
    const drawn = still ? full : written

    useEffect(() => {
        if (still) return
        if (reached) {
            const t0 = performance.now()
            let raf = 0
            const tick = (now: number) => {
                setFading(false)
                const d = writeDrawn((now - t0) / 1000, n)
                setWritten(d)
                if ((d[n - 1] ?? 1) < 1) raf = requestAnimationFrame(tick)
            }
            raf = requestAnimationFrame(tick)
            return () => cancelAnimationFrame(raf)
        }
        // left: the ink fades out, then the strokes reset for the next pass
        const fade = setTimeout(() => setFading(true), 0)
        const reset = setTimeout(() => {
            setWritten(none)
            setFading(false)
        }, 420)
        return () => {
            clearTimeout(fade)
            clearTimeout(reset)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- arrays are derived from strokePaths
    }, [still, reached, n])

    return (
        <span className={cn('relative block', className)} aria-hidden>
            <StrokeDraw
                data-layer="guide"
                strokePaths={strokePaths}
                viewBox={viewBox}
                drawn={full}
                strokeWidth={5.5}
                className="absolute inset-0 size-full text-ink/20"
            />
            <StrokeDraw
                data-layer="ink"
                strokePaths={strokePaths}
                viewBox={viewBox}
                drawn={drawn}
                strokeWidth={5.5}
                className={cn(
                    'absolute inset-0 size-full text-ink',
                    fading && 'transition-opacity duration-[400ms]'
                )}
                style={fading ? { opacity: 0 } : undefined}
            />
        </span>
    )
}
